/**
 * Chat (Werc 2.0 — F4). Canais abertos (todos veem) e conversas diretas (só os
 * participantes), threads, reações, anexos, menções, não lidas e avisos.
 * Escrever funciona offline (vai pela fila); anexo precisa de internet.
 */
import { batch, createEffect, createMemo, createResource, createSignal, For, on, onCleanup, Show } from 'solid-js'
import { Button } from '@ui/components/Button'
import { Input } from '@ui/components/Input'
import { Resize } from '@core/component/Resize'
import { isMobileWidth } from '@core/mobile/mobileWidth'
import type { ChatConversa, ChatMensagem, UsuarioPublico } from '@shared/types'
import { api, versaoDados } from '../../nucleo/cliente'
import { mensagemErro } from '../../lib/erro'
import { AreaTexto, Campo, confirmar, Erro, Formulario } from '../../ui/kit'
import { conversaAberta, permissao, pedirPermissao, setChatNaTela, setConversaAberta } from './avisos'
import { Compositor } from './Compositor'
import { Avatar, Mensagem, TextoRico } from './Mensagem'
import { dataLocal, dia, quando } from './util'

const AGRUPAR_MS = 5 * 60_000

export function Chat(props: { usuario: UsuarioPublico }) {
  const [recarga, setRecarga] = createSignal(0)
  const recarregar = () => setRecarga((n) => n + 1)
  const [conversas] = createResource(() => ({ v: versaoDados(), r: recarga() }), () => api.chat.conversas())
  const [usuarios] = createResource(() => versaoDados(), () => api.chat.usuarios())
  const [busca, setBusca] = createSignal('')
  const [resultados] = createResource(() => (busca().trim().length >= 2 ? busca().trim() : false), (t) => api.chat.buscar(t))
  const [novoCanal, setNovoCanal] = createSignal(false)
  const [novaDireta, setNovaDireta] = createSignal(false)
  const [erro, setErro] = createSignal<string | null>(null)
  const [arquivados, setArquivados] = createSignal(false)

  const atual = () => (conversas.latest ?? []).find((c) => c.id === conversaAberta()) ?? null
  // 1ª vez (nenhuma escolhida ainda): abre um CANAL, nunca uma conversa direta —
  // abrir sozinha uma direta não lida a marcaria como lida sem a pessoa escolher.
  createEffect(() => {
    const l = conversas.latest
    if (l && conversaAberta() === null && !isMobileWidth() && l.length) {
      const canal = l.filter((c) => c.tipo === 'canal' && !c.arquivado).sort((a, b) => a.naoLidas - b.naoLidas || a.nome.localeCompare(b.nome))[0]
      if (canal) setConversaAberta(canal.id)
    }
  })
  // (o clique no aviso do computador é tratado na casca: abre o Chat já na conversa)
  // A conversa escolhida fica guardada ao sair do Chat e voltar (achado: voltar abria
  // sozinha a conversa mais recente e a marcava como lida). "Chat na tela" é à parte.
  setChatNaTela(true)
  onCleanup(() => setChatNaTela(false))

  const canais = () => (conversas.latest ?? []).filter((c) => c.tipo === 'canal' && (arquivados() || !c.arquivado)).sort((a, b) => a.nome.localeCompare(b.nome))
  const diretas = () => (conversas.latest ?? []).filter((c) => c.tipo === 'direta')

  const Item = (p: { c: ChatConversa }) => (
    <button
      type="button"
      class="w-full flex items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm"
      classList={{ 'bg-accent/15 text-ink': conversaAberta() === p.c.id, 'text-ink-muted hover:bg-hover': conversaAberta() !== p.c.id, 'opacity-60': p.c.arquivado }}
      onClick={() => (setBusca(''), setConversaAberta(p.c.id))}
    >
      <Show when={p.c.tipo === 'direta'} fallback={<span class="w-5 text-center text-ink-placeholder">#</span>}>
        <Avatar id={p.c.participantes.find((x) => x !== props.usuario.id) ?? props.usuario.id} nome={p.c.nome} tamanho="p" />
      </Show>
      <span class="truncate flex-1" classList={{ 'font-semibold text-ink': p.c.naoLidas > 0 && !p.c.silenciado }}>{p.c.nome}</span>
      <Show when={p.c.silenciado}>
        <span class="text-[10px] text-ink-placeholder" title="Silenciada">🔕</span>
      </Show>
      <Show when={p.c.naoLidas > 0 && !p.c.silenciado}>
        <span class="rounded-full px-1.5 text-[10px] font-semibold tabular-nums" classList={{ 'bg-accent text-white': p.c.mencoes > 0, 'bg-hover text-ink': p.c.mencoes === 0 }}>
          {p.c.naoLidas}
        </span>
      </Show>
    </button>
  )

  const lista = (
    <div class="h-full flex flex-col bg-panel">
      <div class="p-3 border-b border-edge flex flex-col gap-2">
        <div class="flex items-center gap-2">
          <h1 class="text-base font-semibold text-ink mr-auto">Chat</h1>
          <Button variant="ghost" size="sm" onClick={() => setNovaDireta(true)} title="Nova conversa direta">+ Conversa</Button>
          <Button variant="ghost" size="sm" onClick={() => setNovoCanal(true)} title="Novo canal">+ Canal</Button>
        </div>
        <Input placeholder="Buscar mensagens…" value={busca()} onInput={(e) => setBusca(e.currentTarget.value)} />
      </div>
      <div class="flex-1 min-h-0 overflow-auto p-2 flex flex-col gap-4">
        <section>
          <div class="px-2 pb-1 flex items-center text-[11px] font-medium uppercase tracking-wide text-ink-subtle">
            Canais
            <button type="button" class="ml-auto normal-case tracking-normal text-ink-placeholder hover:text-ink" onClick={() => setArquivados(!arquivados())}>
              {arquivados() ? 'esconder arquivados' : 'ver arquivados'}
            </button>
          </div>
          <Show when={canais().length > 0} fallback={<p class="px-2 text-xs text-ink-placeholder">Nenhum canal ainda. Crie o primeiro em "+ Canal".</p>}>
            <For each={canais()}>{(c) => <Item c={c} />}</For>
          </Show>
        </section>
        <section>
          <div class="px-2 pb-1 text-[11px] font-medium uppercase tracking-wide text-ink-subtle">Conversas diretas</div>
          <Show when={diretas().length > 0} fallback={<p class="px-2 text-xs text-ink-placeholder">Nenhuma conversa direta.</p>}>
            <For each={diretas()}>{(c) => <Item c={c} />}</For>
          </Show>
        </section>
        <Show when={permissao() === 'default'}>
          <button type="button" class="mx-2 rounded-md border border-dashed border-edge px-3 py-2 text-left text-xs text-ink-muted hover:bg-hover" onClick={() => void pedirPermissao()}>
            🔔 Ativar avisos no computador para mensagens diretas e menções
          </button>
        </Show>
      </div>
    </div>
  )

  const conteudo = () => (
    <Show
      when={!busca().trim()}
      fallback={<Resultados busca={busca()} itens={resultados.latest ?? []} conversas={conversas.latest ?? []} carregando={resultados.loading} aoAbrir={(id) => (setBusca(''), setConversaAberta(id))} />}
    >
      {/* chave = só o id: atualizar a lista não recria a conversa (perderia thread aberta, texto digitado e rolagem) */}
      <Show when={atual() ? atual()!.id : null} keyed fallback={<div class="h-full flex items-center justify-center text-sm text-ink-subtle">Escolha uma conversa.</div>}>
        {(_id: number) => <Conversa conversa={atual()!} usuario={props.usuario} usuarios={usuarios.latest ?? []} aoVoltar={() => setConversaAberta(null)} aoMudar={recarregar} />}
      </Show>
    </Show>
  )

  return (
    <div class="h-full relative">
      <Erro mensagem={erro()} />
      <Show
        when={!isMobileWidth()}
        fallback={<div class="h-full">{conversaAberta() === null && !busca().trim() ? lista : conteudo()}</div>}
      >
        <Resize.Zone direction="horizontal" gutter={8} minSize={220}>
          <Resize.Panel id="chat-lista" minSize={220} maxSize={420} target={{ kind: 'px', px: 280 }}>
            <div class="h-full border-r border-edge">{lista}</div>
          </Resize.Panel>
          <Resize.Panel id="chat-conversa" minSize={420}>
            {conteudo()}
          </Resize.Panel>
        </Resize.Zone>
      </Show>
      <NovoCanal aberto={novoCanal()} aoFechar={() => setNovoCanal(false)} aoCriar={(id) => (setNovoCanal(false), recarregar(), setConversaAberta(id))} />
      <NovaDireta
        aberto={novaDireta()}
        eu={props.usuario.id}
        usuarios={usuarios.latest ?? []}
        aoFechar={() => setNovaDireta(false)}
        aoAbrir={async (ids) => {
          try {
            const { id } = await api.chat.abrirDireta(ids)
            batch(() => (setNovaDireta(false), recarregar(), setConversaAberta(id)))
          } catch (e) {
            setErro(mensagemErro(e))
          }
        }}
      />
    </div>
  )
}

// ---------------------------------------------------------------- conversa
function agrupar(lista: ChatMensagem[]) {
  return lista.map((m, i) => {
    const ant = lista[i - 1]
    const novoDia = !ant || dataLocal(ant.criadoEm).toDateString() !== dataLocal(m.criadoEm).toDateString()
    const agrupada = !!ant && !novoDia && ant.autorId === m.autorId && !ant.excluido && dataLocal(m.criadoEm).getTime() - dataLocal(ant.criadoEm).getTime() < AGRUPAR_MS
    return { m, novoDia, agrupada }
  })
}

function Conversa(props: { conversa: ChatConversa; usuario: UsuarioPublico; usuarios: import('@shared/types').ChatUsuario[]; aoVoltar: () => void; aoMudar: () => void }) {
  let caixa: HTMLDivElement | undefined
  const [antigas, setAntigas] = createSignal<ChatMensagem[]>([])
  const [semMais, setSemMais] = createSignal(false)
  const [threadId, setThreadId] = createSignal<number | null>(null)
  const [erro, setErro] = createSignal<string | null>(null)
  const [editar, setEditar] = createSignal(false)
  const [recentes] = createResource(() => ({ id: props.conversa.id, v: versaoDados() }), ({ id }) => api.chat.mensagens(id, { limite: 60 }))
  const eu = () => ({ login: props.usuario.login, nome: props.usuario.nome })
  const todas = createMemo(() => {
    const r = recentes.latest ?? []
    const ids = new Set(r.map((m) => m.id))
    return [...antigas().filter((m) => !ids.has(m.id)), ...r]
  })

  // rolar para o fim ao abrir e quando chega mensagem (se a pessoa já estava no fim)
  let noFim = true
  const rolarFim = () => requestAnimationFrame(() => caixa && (caixa.scrollTop = caixa.scrollHeight))
  createEffect(on(() => todas().length, (n, ant) => {
    if (!ant || noFim) rolarFim()
  }))
  // marcar como lido o que está na tela (também quando a janela volta a ficar visível)
  const [visivel, setVisivel] = createSignal(document.visibilityState === 'visible')
  let jaMarcado = 0
  const aoMudarVisibilidade = () => setVisivel(document.visibilityState === 'visible')
  document.addEventListener('visibilitychange', aoMudarVisibilidade)
  onCleanup(() => document.removeEventListener('visibilitychange', aoMudarVisibilidade))
  createEffect(() => {
    // até a última mensagem que este computador tem, inclusive respostas de thread.
    // Nunca pede duas vezes a mesma marca: cada pedido é uma gravação na fila (achado: laço
    // de "marcar lido" encheu a fila e estourou o limite de pedidos do servidor).
    const ate = props.conversa.ultimaId
    if (props.conversa.naoLidas > 0 && ate > jaMarcado && visivel()) {
      jaMarcado = ate
      void api.chat.marcarLido(props.conversa.id, ate).then(props.aoMudar).catch(() => (jaMarcado = 0))
    }
  })

  const carregarAnteriores = async () => {
    const primeira = todas()[0]
    if (!primeira) return
    const altura = caixa?.scrollHeight ?? 0
    const mais = await api.chat.mensagens(props.conversa.id, { antesDe: primeira.id, limite: 60 })
    if (mais.length < 60) setSemMais(true)
    setAntigas([...mais, ...antigas()])
    requestAnimationFrame(() => caixa && (caixa.scrollTop = caixa.scrollHeight - altura))
  }
  const acao = (fn: () => Promise<unknown>) => {
    setErro(null)
    fn().then(props.aoMudar).catch((e) => setErro(mensagemErro(e)))
  }
  const apagar = async (id: number) => {
    if (await confirmar('Apagar mensagem?', 'Fica "mensagem apagada" no lugar.', { rotulo: 'Apagar', perigo: true })) acao(() => api.chat.apagar(id))
  }

  const corpo = (
    <div class="h-full flex flex-col">
      <div class="h-12 shrink-0 border-b border-edge px-4 flex items-center gap-3">
        <Show when={isMobileWidth()}>
          <button type="button" class="text-sm text-ink-muted" onClick={props.aoVoltar}>←</button>
        </Show>
        <div class="min-w-0 flex-1">
          <div class="font-semibold text-ink truncate">{props.conversa.tipo === 'canal' ? `# ${props.conversa.nome}` : props.conversa.nome}</div>
          <Show when={props.conversa.descricao}>
            <div class="text-xs text-ink-subtle truncate">{props.conversa.descricao}</div>
          </Show>
        </div>
        <Button variant="ghost" size="sm" onClick={() => acao(() => api.chat.silenciar(props.conversa.id, !props.conversa.silenciado))}>
          {props.conversa.silenciado ? '🔔 Reativar avisos' : '🔕 Silenciar'}
        </Button>
        <Show when={props.conversa.tipo === 'canal'}>
          <Button variant="ghost" size="sm" onClick={() => setEditar(true)}>Canal…</Button>
        </Show>
      </div>
      <Erro mensagem={erro()} />
      <div ref={caixa} class="ped-rolagem flex-1 min-h-0 overflow-auto py-2" onScroll={(e) => (noFim = e.currentTarget.scrollHeight - e.currentTarget.scrollTop - e.currentTarget.clientHeight < 80)}>
        <Show when={todas().length >= 60 && !semMais()}>
          <div class="text-center py-2">
            <Button variant="ghost" size="sm" onClick={() => void carregarAnteriores()}>Carregar mensagens anteriores</Button>
          </div>
        </Show>
        <Show when={recentes.latest && todas().length === 0}>
          <div class="px-6 py-10 text-center text-sm text-ink-subtle">
            {props.conversa.tipo === 'canal' ? `Este é o começo de #${props.conversa.nome}.` : 'Comece a conversa.'}
          </div>
        </Show>
        <For each={agrupar(todas())}>
          {(g) => (
            <>
              <Show when={g.novoDia}>
                <div class="flex items-center gap-3 px-4 py-2">
                  <span class="h-px flex-1 bg-edge" />
                  <span class="text-[11px] font-medium text-ink-subtle first-letter:uppercase">{dia(g.m.criadoEm)}</span>
                  <span class="h-px flex-1 bg-edge" />
                </div>
              </Show>
              <Mensagem
                m={g.m}
                agrupada={g.agrupada}
                meuId={props.usuario.id}
                eu={eu()}
                ehAdmin={props.usuario.perfil === 'ADMIN'}
                aoResponder={() => setThreadId(g.m.id)}
                aoReagir={(e) => acao(() => api.chat.reagir(g.m.id, e))}
                aoEditar={(t) => acao(() => api.chat.editar(g.m.id, t))}
                aoApagar={() => void apagar(g.m.id)}
              />
            </>
          )}
        </For>
      </div>
      <Compositor
        placeholder={props.conversa.tipo === 'canal' ? `Mensagem em #${props.conversa.nome}` : `Mensagem para ${props.conversa.nome}`}
        usuarios={props.usuarios}
        desabilitado={props.conversa.arquivado ? 'Canal arquivado (só leitura).' : null}
        aoEnviar={async (texto, anexos) => {
          noFim = true
          await api.chat.enviar({ canalId: props.conversa.id, texto, anexos })
          props.aoMudar()
        }}
      />
      <Show when={props.conversa.tipo === 'canal'}>
        <EditarCanal aberto={editar()} conversa={props.conversa} aoFechar={() => setEditar(false)} aoSalvar={() => (setEditar(false), props.aoMudar())} />
      </Show>
    </div>
  )

  return (
    <Show when={threadId()} fallback={corpo}>
      {(raiz) => (
        <Show
          when={!isMobileWidth()}
          fallback={<Thread raizId={raiz()} conversa={props.conversa} usuario={props.usuario} usuarios={props.usuarios} aoFechar={() => setThreadId(null)} aoMudar={props.aoMudar} />}
        >
          <Resize.Zone direction="horizontal" gutter={8} minSize={300}>
            <Resize.Panel id="chat-mensagens" minSize={360}>{corpo}</Resize.Panel>
            <Resize.Panel id="chat-thread" minSize={300} maxSize={640} target={{ kind: 'px', px: 380 }}>
              <div class="h-full border-l border-edge">
                <Thread raizId={raiz()} conversa={props.conversa} usuario={props.usuario} usuarios={props.usuarios} aoFechar={() => setThreadId(null)} aoMudar={props.aoMudar} />
              </div>
            </Resize.Panel>
          </Resize.Zone>
        </Show>
      )}
    </Show>
  )
}

function Thread(props: { raizId: number; conversa: ChatConversa; usuario: UsuarioPublico; usuarios: import('@shared/types').ChatUsuario[]; aoFechar: () => void; aoMudar: () => void }) {
  let caixa: HTMLDivElement | undefined
  const [lista] = createResource(() => ({ id: props.raizId, v: versaoDados() }), ({ id }) => api.chat.thread(id))
  const [erro, setErro] = createSignal<string | null>(null)
  createEffect(on(() => lista.latest?.length, () => requestAnimationFrame(() => caixa && (caixa.scrollTop = caixa.scrollHeight))))
  const acao = (fn: () => Promise<unknown>) => (setErro(null), fn().then(props.aoMudar).catch((e) => setErro(mensagemErro(e))))
  return (
    <div class="h-full flex flex-col bg-panel">
      <div class="h-12 shrink-0 border-b border-edge px-4 flex items-center">
        <span class="font-semibold text-ink mr-auto">Thread</span>
        <button type="button" class="text-sm text-ink-subtle hover:text-ink px-2 py-1 rounded hover:bg-hover" onClick={props.aoFechar}>Fechar</button>
      </div>
      <Erro mensagem={erro()} />
      <div ref={caixa} class="ped-rolagem flex-1 min-h-0 overflow-auto py-2">
        <For each={lista.latest ?? []}>
          {(m, i) => (
            <>
              <Mensagem
                m={m}
                agrupada={false}
                naThread
                meuId={props.usuario.id}
                eu={{ login: props.usuario.login, nome: props.usuario.nome }}
                ehAdmin={props.usuario.perfil === 'ADMIN'}
                aoReagir={(e) => acao(() => api.chat.reagir(m.id, e))}
                aoEditar={(t) => acao(() => api.chat.editar(m.id, t))}
                aoApagar={() => acao(() => api.chat.apagar(m.id))}
              />
              <Show when={i() === 0}>
                <div class="px-4 py-2 text-[11px] text-ink-subtle border-b border-edge-muted">
                  {(lista.latest?.length ?? 1) - 1} resposta(s)
                </div>
              </Show>
            </>
          )}
        </For>
      </div>
      <Compositor
        placeholder="Responder na thread"
        usuarios={props.usuarios}
        desabilitado={props.conversa.arquivado ? 'Canal arquivado (só leitura).' : null}
        aoEnviar={async (texto, anexos) => {
          await api.chat.enviar({ canalId: props.conversa.id, texto, anexos, threadId: props.raizId })
          props.aoMudar()
        }}
      />
    </div>
  )
}

function Resultados(props: { busca: string; itens: ChatMensagem[]; conversas: ChatConversa[]; carregando: boolean; aoAbrir: (canalId: number) => void }) {
  const nome = (id: number) => {
    const c = props.conversas.find((x) => x.id === id)
    return c ? (c.tipo === 'canal' ? `# ${c.nome}` : c.nome) : ''
  }
  return (
    <div class="h-full overflow-auto p-4 flex flex-col gap-2">
      <div class="text-sm text-ink-subtle">{props.carregando ? 'Buscando…' : `${props.itens.length} resultado(s) para "${props.busca.trim()}"`}</div>
      <For each={props.itens}>
        {(m) => (
          <button type="button" class="rounded-lg border border-edge p-3 text-left hover:bg-hover" onClick={() => props.aoAbrir(m.canalId)}>
            <div class="flex items-baseline gap-2 text-xs text-ink-subtle">
              <span class="font-medium text-ink">{m.autor}</span>
              <span>{nome(m.canalId)}</span>
              <span class="ml-auto">{quando(m.criadoEm)}</span>
            </div>
            <div class="mt-1 text-sm text-ink line-clamp-3">
              <TextoRico texto={m.texto} />
            </div>
          </button>
        )}
      </For>
    </div>
  )
}

// ---------------------------------------------------------------- diálogos
function NovoCanal(props: { aberto: boolean; aoFechar: () => void; aoCriar: (id: number) => void }) {
  const [nome, setNome] = createSignal('')
  const [descricao, setDescricao] = createSignal('')
  const [erro, setErro] = createSignal<string | null>(null)
  createEffect(on(() => props.aberto, (a) => a && (setNome(''), setDescricao(''), setErro(null))))
  const criar = async () => {
    try {
      const { id } = await api.chat.criarCanal({ nome: nome(), descricao: descricao() || null })
      props.aoCriar(id)
    } catch (e) {
      setErro(mensagemErro(e))
    }
  }
  return (
    <Formulario
      aberto={props.aberto}
      titulo="Novo canal"
      largura={460}
      aoFechar={props.aoFechar}
      aoEnviar={() => void criar()}
      rodape={
        <>
          <Button type="button" variant="ghost" onClick={() => props.aoFechar()}>Cancelar</Button>
          <Button type="submit" variant="accent">Criar canal</Button>
        </>
      }
    >
      <Erro mensagem={erro()} />
      <Campo rotulo="Nome" dica="Todos os usuários veem os canais. Ex.: comercial, importacoes, avisos">
        <Input value={nome()} onInput={(e) => setNome(e.currentTarget.value)} placeholder="comercial" />
      </Campo>
      <Campo rotulo="Descrição (opcional)">
        <AreaTexto value={descricao()} onInput={(e) => setDescricao(e.currentTarget.value)} class="min-h-14" />
      </Campo>
    </Formulario>
  )
}

function NovaDireta(props: { aberto: boolean; eu: number; usuarios: import('@shared/types').ChatUsuario[]; aoFechar: () => void; aoAbrir: (ids: number[]) => void }) {
  const [sel, setSel] = createSignal<Set<number>>(new Set())
  const [filtro, setFiltro] = createSignal('')
  createEffect(on(() => props.aberto, (a) => a && (setSel(new Set<number>()), setFiltro(''))))
  const lista = () => props.usuarios.filter((u) => u.id !== props.eu && u.nome.toLowerCase().includes(filtro().toLowerCase()))
  const alternar = (id: number) => {
    const n = new Set(sel())
    n.has(id) ? n.delete(id) : n.add(id)
    setSel(n)
  }
  return (
    <Formulario
      aberto={props.aberto}
      titulo="Nova conversa direta"
      largura={460}
      aoFechar={props.aoFechar}
      aoEnviar={() => sel().size && props.aoAbrir([...sel()])}
      rodape={
        <>
          <Button type="button" variant="ghost" onClick={() => props.aoFechar()}>Cancelar</Button>
          <Button type="submit" variant="accent" disabled={sel().size === 0}>Abrir conversa</Button>
        </>
      }
    >
      <p class="text-xs text-ink-subtle">Só as pessoas escolhidas veem esta conversa (nem o servidor a envia para os outros computadores).</p>
      <Input placeholder="Buscar pessoa" value={filtro()} onInput={(e) => setFiltro(e.currentTarget.value)} />
      <div class="max-h-72 overflow-auto rounded-lg border border-edge divide-y divide-edge-muted">
        <For each={lista()}>
          {(u) => (
            <label class="flex cursor-pointer items-center gap-3 px-3 py-2 hover:bg-hover">
              <input type="checkbox" checked={sel().has(u.id)} onChange={() => alternar(u.id)} />
              <Avatar id={u.id} nome={u.nome} tamanho="p" />
              <span class="text-sm text-ink">{u.nome}</span>
              <span class="ml-auto text-xs text-ink-subtle">@{u.login}</span>
            </label>
          )}
        </For>
      </div>
    </Formulario>
  )
}

function EditarCanal(props: { aberto: boolean; conversa: ChatConversa; aoFechar: () => void; aoSalvar: () => void }) {
  const [nome, setNome] = createSignal('')
  const [descricao, setDescricao] = createSignal('')
  const [erro, setErro] = createSignal<string | null>(null)
  createEffect(on(() => props.aberto, (a) => a && (setNome(props.conversa.nome), setDescricao(props.conversa.descricao ?? ''), setErro(null))))
  const salvar = async (arquivado?: boolean) => {
    try {
      await api.chat.editarCanal(props.conversa.id, { nome: nome(), descricao: descricao() || null, ...(arquivado !== undefined ? { arquivado } : {}) })
      props.aoSalvar()
    } catch (e) {
      setErro(mensagemErro(e))
    }
  }
  return (
    <Formulario
      aberto={props.aberto}
      titulo={`Canal #${props.conversa.nome}`}
      largura={460}
      aoFechar={props.aoFechar}
      aoEnviar={() => void salvar()}
      rodape={
        <>
          <Button type="button" variant="ghost" class="mr-auto" onClick={() => void salvar(!props.conversa.arquivado)}>
            {props.conversa.arquivado ? 'Desarquivar' : 'Arquivar canal'}
          </Button>
          <Button type="button" variant="ghost" onClick={() => props.aoFechar()}>Cancelar</Button>
          <Button type="submit" variant="accent">Salvar</Button>
        </>
      }
    >
      <Erro mensagem={erro()} />
      <Campo rotulo="Nome"><Input value={nome()} onInput={(e) => setNome(e.currentTarget.value)} /></Campo>
      <Campo rotulo="Descrição"><AreaTexto value={descricao()} onInput={(e) => setDescricao(e.currentTarget.value)} class="min-h-14" /></Campo>
      <p class="text-xs text-ink-subtle">Só quem criou o canal (ou um ADMIN) pode alterar. Arquivado fica só leitura.</p>
    </Formulario>
  )
}
